import api from './api';
import type {
  RestoreSettings,
  PasswordChangeRequest,
  PasswordChangeHistory,
  PasswordPolicySetting,
} from '../types/settings.types';
import type { ApiResponse } from '../types/api.types';

export const settingsService = {
  // ADR-049: 密碼有效期限（使用者管理頁）— the value in force and its source.
  async getPasswordPolicy(): Promise<PasswordPolicySetting> {
    const response = await api.get<ApiResponse<PasswordPolicySetting>>('/api/v1/settings/password-policy');
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to load password policy');
  },

  // skipGlobalError: the range error lands on the dialog's field (QA #68).
  async updatePasswordPolicy(maxAgeDays: number): Promise<PasswordPolicySetting> {
    const response = await api.put<ApiResponse<PasswordPolicySetting>>(
      '/api/v1/settings/password-policy',
      { max_age_days: maxAgeDays },
      { skipGlobalError: true },
    );
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to update password policy');
  },

  async resetPasswordPolicy(): Promise<PasswordPolicySetting> {
    const response = await api.delete<ApiResponse<PasswordPolicySetting>>('/api/v1/settings/password-policy', {
      skipGlobalError: true,
    });
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to reset password policy');
  },

  // 獲取還原設定
  async getRestoreSettings(): Promise<RestoreSettings> {
    const response = await api.get<ApiResponse<any>>('/api/v1/settings/restore');
    const data = response.data.data;

    // Map backend field names to frontend names with defaults
    return {
      defaultRestorePath: data?.restore_path || '/tmp/restore',
      preserveDirectory: data?.preserve_permissions ?? true,
      autoRename: data?.overwrite_existing ?? false,
      createSubfolders: data?.create_subfolders ?? true,
      preferredWatcherId: data?.preferred_watcher_id ?? null,
    };
  },

  // 更新還原設定
  async updateRestoreSettings(settings: RestoreSettings): Promise<void> {
    // Map frontend field names to backend names
    await api.put('/api/v1/settings/restore', {
      restore_path: settings.defaultRestorePath,
      preserve_permissions: settings.preserveDirectory,
      overwrite_existing: settings.autoRename,
      create_subfolders: settings.createSubfolders,
      preferred_watcher_id: settings.preferredWatcherId ?? null,
    });
  },

  // 修改密碼
  async changePassword(request: PasswordChangeRequest): Promise<void> {
    // skipGlobalError: the Password page shows the error itself keyed on the
    // code (QA #16), so the global snackbar does not also pop the raw message.
    await api.put(
      '/api/v1/auth/change-password',
      {
        old_password: request.oldPassword,
        new_password: request.newPassword,
      },
      { skipGlobalError: true },
    );
  },

  // 獲取密碼修改歷史
  async getPasswordHistory(): Promise<PasswordChangeHistory[]> {
    // Backend PasswordHistoryItem: { id, changed_at, ip_address }
    interface BackendPasswordHistoryItem {
      id: string;
      changed_at: string;
      ip_address: string;
    }
    const response = await api.get<ApiResponse<BackendPasswordHistoryItem[]>>('/api/v1/auth/password-history');
    const items = response.data.data || [];
    return items.map((item): PasswordChangeHistory => ({
      id: item.id,
      changeTime: item.changed_at ? new Date(item.changed_at).toLocaleString('zh-TW') : '',
      ipAddress: item.ip_address,
    }));
  },
};
